import type { QueryClient } from '@tanstack/react-query';

/**
 * SORGU ANAHTARI FABRIKASI — pazaryeri (kur / talep / teklif) tarafi.
 *
 * ============================ NEDEN FABRIKA ================================
 * Mobil tarafta bir HATA SINIFI vardi: "kayit olustu ama listede yok". Sebep
 * her seferinde ayniydi — bir mutasyon calisiyor, ama guncelledigi listeyi
 * gecersiz kilmiyor; liste onbellekte bayat kaliyor ve kullanici kaydini
 * goremiyor. Elle yazilan anahtar dizileri bunu kacinilmaz kilar: bir harf
 * farki sessizce "hicbir sorguyla eslesmeyen" bir gecersiz kilma uretir ve
 * hicbir yerde hata vermez.
 *
 * Bu yuzden anahtarlar TEK YERDEN uretilir ve gecersiz kilma da tek yerden
 * yapilir (invalidateMarketplaceLists). Bir ucu ekleyen kisi "hangi listeleri
 * bozdum" sorusunu dusunmek zorunda kalmasin diye kaba davraniyoruz: talep ya
 * da teklif degistiginde HER IKI listenin de tum sayfalari gecersiz olur.
 * Sunucu tarafi sayfalama zaten `snapshotAt` ile tutarli; fazladan bir istek,
 * gorunmeyen bir kayittan ucuzdur.
 *
 * ============================== HIYERARSI ==================================
 *   ['rates']                                   kur bulteni
 *   ['tasks']                                   -> tum talep sorgulari
 *   ['tasks','list', paramsKey, offset]         sayfalanmis liste
 *   ['tasks','detail', taskId]                  tek talep
 *   ['offers']                                  -> tum teklif sorgulari
 *   ['offers','list', paramsKey, offset]        sayfalanmis liste
 *   ['task-accounts', userId]                   islem icin banka hesaplari
 *
 * Onek eslemesi TanStack'in varsayilanidir: ['tasks'] ile yapilan gecersiz
 * kilma altindaki HER seyi kapsar.
 * ========================================================================== */

export const marketplaceKeys = {
  rates: () => ['rates'] as const,

  tasks: () => ['tasks'] as const,
  taskLists: () => ['tasks', 'list'] as const,
  taskList: (paramsKey: string, offset: number) => ['tasks', 'list', paramsKey, offset] as const,
  taskDetail: (taskId: string) => ['tasks', 'detail', taskId] as const,
  /**
   * Sekme rozeti sayaci. BILINCLI olarak ['tasks'] agacinin ALTINDA: boylece
   * invalidateMarketplaceLists sayaclari da tazeliyor ve "liste dogru ama sayac eski"
   * durumu olusamiyor. Panel sayaclari (dashboard/query-keys.ts) ayri bir agacta oldugu
   * icin tam bu sorunu yasiyordu.
   */
  taskCount: (paramsKey: string) => ['tasks', 'count', paramsKey] as const,

  offers: () => ['offers'] as const,
  offerLists: () => ['offers', 'list'] as const,
  offerList: (paramsKey: string, offset: number) => ['offers', 'list', paramsKey, offset] as const,
  /**
   * Kabuktaki kazanma bildiriminin okumasi (2026-10-02, features/offers/useWinNotice.ts).
   * ['offers'] agacinda: teklif mutasyonlari onu da tazeler. 'list' DISINDA: liste onbellegini
   * tarayan/duzelten yardimcilar (setQueriesData) bu tek sayfalik okumaya dokunmasin.
   */
  winNotice: (userId: string) => ['offers', 'win-notice', userId] as const,

  /**
   * Hesap listesi. Onek BILINCLI olarak 'accounts' DEGIL: hesap YONETIMI
   * ekranlari (src/features/accounts) kendi ['accounts', …] agacini kullaniyor
   * ve o agaci buradan gecersiz kilmak, iki ayri ozelligin birbirinin
   * onbellegini sessizce bosaltmasi demek olurdu.
   */
  taskAccounts: (userId: string) => ['task-accounts', userId] as const,
} as const;

/**
 * BIR TALEP YA DA TEKLIF DEGISTI — ilgili tum listeleri gecersiz kil.
 *
 * Talep ve teklif AYNI ANDA gecersiz olur ve bu kasitli: bir teklifin kabulu
 * gorevin fazini degistirir, bir talebin iptali teklif listelerindeki satirlari
 * olu birakir. Ikisini ayirmak, "hangi mutasyon hangi listeyi bozar" tablosunu
 * her yeni uc ile birlikte guncellemeyi gerektirirdi; o tablo bir kez
 * guncellenmedigi anda hata geri gelir.
 */
export const invalidateMarketplaceLists = (client: QueryClient): void => {
  void client.invalidateQueries({ queryKey: marketplaceKeys.tasks() });
  void client.invalidateQueries({ queryKey: marketplaceKeys.offers() });
};

/** Liste anahtarinin imzasi `taskStatus: 'new'` filtresi tasiyor mu (Acik Talepler / piyasa). */
const isOpenTaskListKey = (queryKey: readonly unknown[]): boolean => {
  const signature = queryKey[2];
  if (typeof signature !== 'string') return false;
  try {
    return (JSON.parse(signature) as { taskStatus?: unknown }).taskStatus === 'new';
  } catch {
    return false;
  }
};

/**
 * KENDI GECISINDEN SONRA — talebi, artik ait olmadigi acik talep listelerinin onbelleginden dusur.
 *
 * Gecersiz kilma yalniz AKTIF sorgulari hemen yeniden ceker. Teklif kabulu baska bir rotada
 * (/musteri/talepler/:id) yapildigi icin Acik Talepler sorgusu o an pasif. Musteri geri donunce
 * once onbellekteki ESKI sayfa ciziliyordu (onaylanan talep hala icinde), satir ancak yeniden
 * cekme bitince dusuyordu (madde 5). Gidilen sekme zaten gecersiz kilindigi icin sunucudan
 * dogru haliyle gelir.
 */
export const dropTaskFromOpenLists = (client: QueryClient, taskId: string | number): void => {
  client.setQueriesData<{ items: { id?: unknown }[] }>(
    { queryKey: marketplaceKeys.taskLists(), predicate: (query) => isOpenTaskListKey(query.queryKey) },
    (page) =>
      page && { ...page, items: page.items.filter((item) => String(item.id) !== String(taskId)) },
  );
};
